import { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Download, Upload, Warning, CheckCircle, ImageSquare, ArrowCounterClockwise } from "@phosphor-icons/react";

/**
 * MenuCsvImport — dropdown-free two-button toolbar for the Menu tab.
 *
 * - Download Template: hits GET /api/menu/import/template, saves a CSV
 *   ready for editing.
 * - Import CSV: opens a modal to pick mode (merge/replace), uploads to
 *   POST /api/menu/import, then renders a summary of created / updated /
 *   skipped counts plus a scrollable list of per-row errors.
 */
const MenuCsvImport = ({ onImportComplete }) => {
  const fileRef = useRef(null);
  const zipRef = useRef(null);
  const [openImport, setOpenImport] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [openSummary, setOpenSummary] = useState(false);
  const [openImages, setOpenImages] = useState(false);
  const [openImagesSummary, setOpenImagesSummary] = useState(false);
  const [openUndo, setOpenUndo] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [restoreCandidate, setRestoreCandidate] = useState(null); // snapshot object being previewed for restore
  const [mode, setMode] = useState("merge");
  const [fetchImages, setFetchImages] = useState(true);
  const [file, setFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [imagesSummary, setImagesSummary] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem("pos_token") || "";
      const res = await fetch(`${API}/menu/import/template`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "crest-pos-menu-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error("Couldn't download template: " + e.message);
    }
  };

  const handleExportMenu = async () => {
    try {
      const token = localStorage.getItem("pos_token") || "";
      const res = await fetch(`${API}/menu/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `crest-pos-menu-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Menu exported");
    } catch (e) {
      toast.error("Couldn't export menu: " + e.message);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error("Pick a CSV file first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Dry-run intentionally skips image fetching so the preview is fast
      // even for menus with hundreds of remote URLs — the real commit
      // below re-uploads with the user's actual fetchImages choice.
      const res = await axios.post(
        `${API}/menu/import?mode=${mode}&fetch_images=false&dry_run=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(res.data);
      setOpenImport(false);
      setOpenPreview(true);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error("Preview failed: " + detail);
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!file) {
      toast.error("File missing — please re-select");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/menu/import?mode=${mode}&fetch_images=${fetchImages}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSummary(res.data);
      setOpenPreview(false);
      setOpenSummary(true);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      if (typeof onImportComplete === "function") onImportComplete();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error("Import failed: " + detail);
    } finally {
      setBusy(false);
    }
  };

  const handleUploadImages = async () => {
    if (!zipFile) {
      toast.error("Pick a ZIP file first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", zipFile);
      const res = await axios.post(`${API}/menu/bulk-images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImagesSummary(res.data);
      setOpenImages(false);
      setOpenImagesSummary(true);
      setZipFile(null);
      if (zipRef.current) zipRef.current.value = "";
      if (typeof onImportComplete === "function") onImportComplete();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error("Image upload failed: " + detail);
    } finally {
      setBusy(false);
    }
  };

  const handleOpenUndo = async () => {
    setOpenUndo(true);
    try {
      const res = await axios.get(`${API}/menu/import/snapshots`);
      setSnapshots(res.data || []);
    } catch (e) {
      toast.error("Couldn't load snapshots: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleRestore = async (snapshotId) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/menu/import/undo/${snapshotId}`);
      const r = res.data.restored || {};
      toast.success(`Restored ${r.items || 0} items, ${r.modifier_templates || 0} groups`);
      setOpenUndo(false);
      setRestoreCandidate(null);
      if (typeof onImportComplete === "function") onImportComplete();
    } catch (e) {
      toast.error("Restore failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const handlePreviewRestore = async (snapshot) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/menu/import/snapshots/${snapshot.id}/diff`);
      // Reuse PreviewDialog by stashing the diff into `preview` state. The
      // dialog auto-detects restore mode from ``counts.items_restore``.
      setPreview(res.data);
      setRestoreCandidate(snapshot);
      setOpenUndo(false);
      setOpenPreview(true);
    } catch (e) {
      toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const c = summary?.counts || {};
  const totalCreated =
    (c.groups_created || 0) + (c.items_created || 0);
  const totalUpdated =
    (c.groups_updated || 0) + (c.items_updated || 0) + (c.categories_updated || 0);
  const totalSkipped = (c.items_skipped || 0) + (c.groups_skipped || 0);

  return (
    <>
      <div className="flex gap-2 flex-wrap" data-testid="menu-csv-toolbar">
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleDownloadTemplate}
          data-testid="csv-download-template-btn"
        >
          <Download size={16} weight="bold" /> Template
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleExportMenu}
          data-testid="csv-export-btn"
        >
          <Download size={16} weight="bold" /> Export
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setOpenImport(true)}
          data-testid="csv-import-btn"
        >
          <Upload size={16} weight="bold" /> Import CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setOpenImages(true)}
          data-testid="images-import-btn"
        >
          <ImageSquare size={16} weight="bold" /> Bulk Images
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleOpenUndo}
          data-testid="csv-undo-btn"
          title="Restore a previous menu state"
        >
          <ArrowCounterClockwise size={16} weight="bold" /> Undo
        </Button>
      </div>

      {/* ---------------- Import dialog ---------------- */}
      <Dialog open={openImport} onOpenChange={setOpenImport}>
        <DialogContent className="sm:max-w-lg" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Menu from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV containing modifiers, modifier groups, categories,
              and menu items. Download the template first if you&apos;re not sure
              of the format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>CSV File</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-secondary file:text-secondary-foreground hover:file:bg-secondary/80"
                data-testid="csv-file-input"
              />
              {file && (
                <p className="text-xs text-muted-foreground" data-testid="csv-file-name">
                  {file.name} • {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Mode</Label>
              <RadioGroup value={mode} onValueChange={setMode} className="gap-3">
                <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                  <RadioGroupItem value="merge" id="mode-merge" data-testid="csv-mode-merge" />
                  <label htmlFor="mode-merge" className="cursor-pointer flex-1">
                    <div className="font-medium text-sm">Merge (recommended)</div>
                    <div className="text-xs text-muted-foreground">
                      Update existing items and modifier groups by name; create anything new. Nothing is deleted.
                    </div>
                  </label>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                  <RadioGroupItem value="replace" id="mode-replace" data-testid="csv-mode-replace" />
                  <label htmlFor="mode-replace" className="cursor-pointer flex-1">
                    <div className="font-medium text-sm text-destructive">Replace</div>
                    <div className="text-xs text-muted-foreground">
                      Wipe ALL menu items and modifier groups first, then import. Cannot be undone.
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </div>

            {mode === "replace" && (
              <Alert variant="destructive" data-testid="csv-replace-warning">
                <Warning size={16} weight="bold" />
                <AlertDescription>
                  This will delete your current menu items and modifier groups
                  before importing. Consider downloading a backup first.
                </AlertDescription>
              </Alert>
            )}

            <label
              className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer"
              data-testid="csv-fetch-images-row"
            >
              <input
                type="checkbox"
                checked={fetchImages}
                onChange={(e) => setFetchImages(e.target.checked)}
                className="mt-1 h-4 w-4 accent-primary"
                data-testid="csv-fetch-images-toggle"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Copy remote images locally</div>
                <div className="text-xs text-muted-foreground">
                  Fetch every https(s) <span className="font-mono">image_url</span> and inline it
                  as a data URI so your item photos stay stable even if the source URL
                  disappears. Unfetchable images are skipped and reported; the item still
                  imports without a photo.
                </div>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpenImport(false)}
              disabled={busy}
              data-testid="csv-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePreview}
              disabled={busy || !file}
              data-testid="csv-upload-btn"
            >
              {busy ? "Analyzing..." : "Preview"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Preview / dry-run dialog ---------------- */}
      <PreviewDialog
        open={openPreview}
        onOpenChange={(v) => {
          setOpenPreview(v);
          if (!v) setRestoreCandidate(null);
        }}
        preview={preview}
        onCancel={() => {
          setOpenPreview(false);
          setRestoreCandidate(null);
        }}
        onCommit={restoreCandidate
          ? () => handleRestore(restoreCandidate.id)
          : handleCommit}
        busy={busy}
      />

      {/* ---------------- Summary dialog ---------------- */}
      <Dialog open={openSummary} onOpenChange={setOpenSummary}>
        <DialogContent className="sm:max-w-lg" data-testid="csv-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
              Import Summary
            </DialogTitle>
            <DialogDescription>
              Mode: <span className="font-medium">{summary?.mode}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-2">
              <SummaryStat label="Created" value={totalCreated} tone="emerald" testid="summary-created" />
              <SummaryStat label="Updated" value={totalUpdated} tone="blue" testid="summary-updated" />
              <SummaryStat label="Skipped" value={totalSkipped} tone="amber" testid="summary-skipped" />
            </div>

            <div className="rounded-lg border border-border p-3 text-xs space-y-1" data-testid="csv-summary-breakdown">
              <div>Modifiers defined: <b>{c.modifiers_defined || 0}</b></div>
              <div>Modifier groups: created <b>{c.groups_created || 0}</b> · updated <b>{c.groups_updated || 0}</b></div>
              <div>Categories: sort updated <b>{c.categories_updated || 0}</b></div>
              <div>Items: created <b>{c.items_created || 0}</b> · updated <b>{c.items_updated || 0}</b></div>
              {(c.images_fetched > 0 || c.images_fetch_failed > 0) && (
                <div data-testid="csv-summary-images">
                  Remote images: fetched <b className="text-emerald-700 dark:text-emerald-300">{c.images_fetched || 0}</b>
                  {c.images_fetch_failed > 0 && (
                    <> · failed <b className="text-amber-700 dark:text-amber-300">{c.images_fetch_failed}</b></>
                  )}
                </div>
              )}
            </div>

            {summary?.errors?.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="csv-errors-section">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                  <Warning size={16} weight="bold" />
                  {summary.errors.length} row(s) skipped
                </div>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                  {summary.errors.map((e, i) => (
                    <div key={i} className="border-b border-amber-500/20 pb-1" data-testid={`csv-error-${i}`}>
                      <span className="font-mono">Row {e.row}</span>
                      {e.name ? <span className="text-muted-foreground"> ({e.name})</span> : null}
                      : {e.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setOpenSummary(false)} data-testid="csv-summary-close-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Bulk Images upload dialog ---------------- */}
      <Dialog open={openImages} onOpenChange={setOpenImages}>
        <DialogContent className="sm:max-w-lg" data-testid="images-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Menu Images</DialogTitle>
            <DialogDescription>
              Drop a .zip of item photos (.jpg / .png / .webp). Each file is
              matched to a menu item by filename — <span className="font-mono">Cheeseburger.png</span>{" "}
              attaches to the item named &ldquo;Cheeseburger&rdquo;. Case and
              spacing are ignored, so <span className="font-mono">cheese burger.jpg</span>{" "}
              also works.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>ZIP File</Label>
              <input
                ref={zipRef}
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-secondary file:text-secondary-foreground hover:file:bg-secondary/80"
                data-testid="images-zip-input"
              />
              {zipFile && (
                <p className="text-xs text-muted-foreground" data-testid="images-zip-name">
                  {zipFile.name} • {(zipFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
            </div>
            <Alert>
              <ImageSquare size={16} weight="bold" />
              <AlertDescription className="text-xs">
                Individual images capped at 8 MB. Unmatched or ambiguous
                filenames are reported so you can rename & retry.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpenImages(false)}
              disabled={busy}
              data-testid="images-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadImages}
              disabled={busy || !zipFile}
              data-testid="images-upload-btn"
            >
              {busy ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Bulk Images summary dialog ---------------- */}
      <Dialog open={openImagesSummary} onOpenChange={setOpenImagesSummary}>
        <DialogContent className="sm:max-w-lg" data-testid="images-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
              Image Upload Summary
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-2">
              <SummaryStat label="Matched" value={imagesSummary?.matched_count || 0} tone="emerald" testid="img-summary-matched" />
              <SummaryStat label="Unmatched" value={imagesSummary?.unmatched_count || 0} tone="amber" testid="img-summary-unmatched" />
              <SummaryStat label="Skipped" value={(imagesSummary?.skipped_count || 0) + (imagesSummary?.ambiguous_count || 0)} tone="blue" testid="img-summary-skipped" />
            </div>

            {imagesSummary?.matched?.length > 0 && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="img-matched-section">
                <div className="text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                  Attached to items
                </div>
                <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                  {imagesSummary.matched.map((m, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 border-b border-emerald-500/20 pb-1">
                      <span className="font-mono truncate">{m.filename}</span>
                      <span className="text-muted-foreground">→ {m.item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {imagesSummary?.unmatched?.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="img-unmatched-section">
                <div className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                  Unmatched (no menu item with that name)
                </div>
                <div className="max-h-32 overflow-y-auto text-xs space-y-1 font-mono">
                  {imagesSummary.unmatched.map((f, i) => (
                    <div key={i} className="border-b border-amber-500/20 pb-1">{f}</div>
                  ))}
                </div>
              </div>
            )}

            {imagesSummary?.ambiguous?.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="img-ambiguous-section">
                <div className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                  Ambiguous (filename matches multiple items)
                </div>
                <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                  {imagesSummary.ambiguous.map((a, i) => (
                    <div key={i} className="border-b border-amber-500/20 pb-1">
                      <span className="font-mono">{a.filename}</span>
                      <span className="text-muted-foreground"> → {a.matches.join(", ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {imagesSummary?.skipped?.length > 0 && (
              <div className="rounded-lg border border-border p-3" data-testid="img-skipped-section">
                <div className="text-sm font-medium mb-2">Skipped</div>
                <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                  {imagesSummary.skipped.map((s, i) => (
                    <div key={i} className="border-b border-border pb-1">
                      <span className="font-mono">{s.filename}</span>
                      <span className="text-muted-foreground"> — {s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setOpenImagesSummary(false)} data-testid="images-summary-close-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Undo / snapshot picker dialog ---------------- */}
      <Dialog open={openUndo} onOpenChange={setOpenUndo}>
        <DialogContent className="sm:max-w-xl" data-testid="csv-undo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowCounterClockwise size={22} weight="bold" className="text-blue-600" />
              Undo Menu Changes
            </DialogTitle>
            <DialogDescription>
              A snapshot is captured before every CSV import. Pick one to
              restore your menu items, modifier groups, and categories to
              that moment. The current state will itself be snapshotted so
              you can undo the undo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="undo-snapshot-list">
            {snapshots.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6" data-testid="undo-empty">
                No snapshots yet. One will be saved automatically the first time you commit a CSV import.
              </div>
            ) : (
              snapshots.map((s) => {
                const when = s.created_at ? new Date(s.created_at) : null;
                const stamp = when ? when.toLocaleString() : "unknown";
                const isRestore = String(s.mode || "").startsWith("pre-restore");
                return (
                  <div
                    key={s.id}
                    className={`rounded-lg border p-3 flex items-start justify-between gap-3 ${s.restored ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}
                    data-testid={`undo-snapshot-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{stamp}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.counts?.items ?? 0} items · {s.counts?.modifier_templates ?? 0} groups
                        {" · "}by {s.created_by || "unknown"}
                        {" · "}
                        <span className={isRestore ? "text-blue-600 dark:text-blue-300" : ""}>
                          {isRestore ? "auto-taken during restore" : `${s.mode} import`}
                        </span>
                        {s.restored ? " · already restored" : ""}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePreviewRestore(s)}
                        disabled={busy}
                        data-testid={`undo-preview-btn-${s.id}`}
                        title="See what will change if you restore this snapshot"
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestore(s.id)}
                        disabled={busy}
                        data-testid={`undo-restore-btn-${s.id}`}
                      >
                        {busy ? "Restoring..." : "Restore"}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpenUndo(false)}
              data-testid="undo-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SummaryStat = ({ label, value, tone, testid }) => {
  const tones = {
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    blue: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    red: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30",
  };
  return (
    <div className={`rounded-lg border p-3 text-center ${tones[tone]}`} data-testid={testid}>
      <div className="text-2xl font-heading">{value}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Preview dialog — renders the dry-run diff so the manager can eyeball
// every planned change (creates in green, updates in blue with per-field
// old→new pairs, skips in amber with the reason) before committing.
// Adapts labels automatically when the payload comes from the snapshot
// restore endpoint (counts.items_restore present → restore variant).
// ---------------------------------------------------------------------------
const PreviewDialog = ({ open, onOpenChange, preview, onCancel, onCommit, busy }) => {
  if (!preview) return null;
  const c = preview.counts || {};
  const d = preview.diff || { groups: [], categories: [], items: [] };

  // Auto-detect restore vs import so we can adjust wording + include the
  // delete action (only reachable through the restore endpoint).
  const isRestore = "items_restore" in c;
  const title = isRestore ? "Preview Restore" : `Preview Changes (${preview.mode} mode)`;
  const description = isRestore
    ? <>Compare the current menu against the snapshot taken on{" "}
      <span className="font-medium">
        {preview.created_at ? new Date(preview.created_at).toLocaleString() : "unknown"}
      </span>. Nothing will change until you click{" "}
      <span className="font-semibold">Confirm Restore</span>.</>
    : <>Nothing has been written yet. Review the diff below and click{" "}
      <span className="font-semibold">Commit Import</span> to apply, or Cancel to abort.</>;
  const commitLabel = isRestore
    ? (busy ? "Restoring..." : "Confirm Restore")
    : (busy ? "Committing..." : "Commit Import");
  const stats = isRestore
    ? [
        { label: "Will Restore", value: (c.items_restore || 0) + (c.groups_restore || 0) + (c.categories_restore || 0), tone: "emerald", testid: "preview-stat-create" },
        { label: "Will Revert", value: (c.items_revert || 0) + (c.groups_revert || 0) + (c.categories_revert || 0), tone: "blue", testid: "preview-stat-update" },
        { label: "Will Delete", value: (c.items_delete || 0) + (c.groups_delete || 0) + (c.categories_delete || 0), tone: "red", testid: "preview-stat-delete" },
      ]
    : [
        { label: "Will Create", value: (c.items_created || 0) + (c.groups_created || 0), tone: "emerald", testid: "preview-stat-create" },
        { label: "Will Update", value: (c.items_updated || 0) + (c.groups_updated || 0) + (c.categories_updated || 0), tone: "blue", testid: "preview-stat-update" },
        { label: "Will Skip", value: (c.items_skipped || 0) + (c.groups_skipped || 0), tone: "amber", testid: "preview-stat-skip" },
      ];

  const itemsByAction = {
    create: (d.items || []).filter((r) => r.action === "create"),
    update: (d.items || []).filter((r) => r.action === "update"),
    skip: (d.items || []).filter((r) => r.action === "skip"),
    delete: (d.items || []).filter((r) => r.action === "delete"),
    unchanged: (d.items || []).filter((r) => r.action === "unchanged"),
  };
  const groupsByAction = {
    create: (d.groups || []).filter((r) => r.action === "create"),
    update: (d.groups || []).filter((r) => r.action === "update"),
    delete: (d.groups || []).filter((r) => r.action === "delete"),
    unchanged: (d.groups || []).filter((r) => r.action === "unchanged"),
  };
  const categoriesByAction = {
    create: (d.categories || []).filter((r) => r.action === "create"),
    update: (d.categories || []).filter((r) => r.action === "update"),
    delete: (d.categories || []).filter((r) => r.action === "delete"),
  };

  const createLabel = isRestore ? "RESTORE" : "CREATE";
  const updateLabel = isRestore ? "REVERT" : "UPDATE";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="csv-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={22} weight="fill" className="text-blue-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 flex-shrink-0">
          {stats.map((s) => (
            <SummaryStat key={s.label} label={s.label} value={s.value} tone={s.tone} testid={s.testid} />
          ))}
        </div>

        {c.images_will_fetch > 0 && (
          <div className="text-xs text-muted-foreground flex-shrink-0" data-testid="preview-image-note">
            <b>{c.images_will_fetch}</b> remote image URL(s) will be downloaded on commit.
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="preview-diff-body">
          {/* --- Modifier Groups --- */}
          {(d.groups || []).length > 0 && (
            <PreviewSection title="Modifier Groups" testid="preview-section-groups">
              {groupsByAction.create.map((row) => (
                <DiffCard key={`gc-${row.name}`} row={row} tone="emerald" label={createLabel} />
              ))}
              {groupsByAction.update.map((row) => (
                <DiffCard key={`gu-${row.name}`} row={row} tone="blue" label={updateLabel} />
              ))}
              {groupsByAction.delete.map((row) => (
                <DiffCard key={`gd-${row.name}`} row={row} tone="red" label="DELETE" />
              ))}
              {groupsByAction.unchanged.length > 0 && (
                <div className="text-xs text-muted-foreground italic pl-2">
                  {groupsByAction.unchanged.length} group(s) unchanged
                </div>
              )}
            </PreviewSection>
          )}

          {/* --- Categories --- */}
          {(d.categories || []).length > 0 && (
            <PreviewSection title="Categories" testid="preview-section-categories">
              {categoriesByAction.create.map((row) => (
                <DiffCard key={`cc-${row.name}`} row={row} tone="emerald" label={createLabel} />
              ))}
              {categoriesByAction.update.map((row) => (
                <DiffCard key={`cu-${row.name}`} row={row} tone="blue" label={updateLabel} />
              ))}
              {categoriesByAction.delete.map((row) => (
                <DiffCard key={`cd-${row.name}`} row={row} tone="red" label="DELETE" />
              ))}
            </PreviewSection>
          )}

          {/* --- Menu Items --- */}
          {(d.items || []).length > 0 && (
            <PreviewSection title="Menu Items" testid="preview-section-items">
              {itemsByAction.create.map((row) => (
                <DiffCard key={`ic-${row.name}`} row={row} tone="emerald" label={createLabel} />
              ))}
              {itemsByAction.update.map((row) => (
                <DiffCard key={`iu-${row.name}`} row={row} tone="blue" label={updateLabel} />
              ))}
              {itemsByAction.delete.map((row) => (
                <DiffCard key={`id-${row.name}`} row={row} tone="red" label="DELETE" />
              ))}
              {itemsByAction.skip.map((row) => (
                <DiffCard key={`is-${row.name}`} row={row} tone="amber" label="SKIP" />
              ))}
              {itemsByAction.unchanged.length > 0 && (
                <div className="text-xs text-muted-foreground italic pl-2">
                  {itemsByAction.unchanged.length} item(s) unchanged
                </div>
              )}
            </PreviewSection>
          )}

          {(preview.errors || []).length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="preview-errors">
              <div className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                {preview.errors.length} row(s) will be skipped
              </div>
              <div className="text-xs space-y-1">
                {preview.errors.map((e, i) => (
                  <div key={i} className="border-b border-amber-500/20 pb-1">
                    <span className="font-mono">Row {e.row}</span>
                    {e.name ? <span className="text-muted-foreground"> ({e.name})</span> : null}
                    : {e.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="preview-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onCommit}
            disabled={busy}
            data-testid="preview-commit-btn"
          >
            {commitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PreviewSection = ({ title, testid, children }) => (
  <div data-testid={testid}>
    <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">
      {title}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const DiffCard = ({ row, tone, label }) => {
  const tones = {
    emerald: "border-emerald-500/40 bg-emerald-500/5",
    blue: "border-blue-500/40 bg-blue-500/5",
    amber: "border-amber-500/40 bg-amber-500/5",
    red: "border-red-500/40 bg-red-500/5",
  };
  const labelTones = {
    emerald: "bg-emerald-600 text-white",
    blue: "bg-blue-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-600 text-white",
  };
  const changes = row.changes || {};
  const changeKeys = Object.keys(changes);
  return (
    <div className={`rounded-lg border p-2 ${tones[tone]}`} data-testid={`diff-row-${row.name}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${labelTones[tone]}`}>
          {label}
        </span>
        <span className="text-sm font-medium truncate">{row.name}</span>
      </div>
      {changeKeys.length > 0 && (
        <div className="text-xs space-y-0.5 pl-1">
          {changeKeys.map((k) => {
            const v = changes[k];
            // Skip reason string
            if (k === "reason") {
              return (
                <div key={k} className="text-amber-700 dark:text-amber-300">
                  Reason: {String(v)}
                </div>
              );
            }
            // Array of strings (modifier_groups delta)
            if (Array.isArray(v) && v.every((x) => typeof x === "string")) {
              return (
                <div key={k}>
                  <span className="font-mono text-muted-foreground">{k}:</span>{" "}
                  {v.map((s, i) => (
                    <span
                      key={i}
                      className={s.startsWith("+") ? "text-emerald-700 dark:text-emerald-300" : s.startsWith("-") ? "text-red-600 dark:text-red-400" : ""}
                    >
                      {s}{i < v.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              );
            }
            // [old, new] pair
            if (Array.isArray(v) && v.length === 2) {
              const [oldV, newV] = v;
              // Options list: [[old items], [new items]]
              if (Array.isArray(oldV) && Array.isArray(newV)) {
                return (
                  <div key={k} className="font-mono">
                    <span className="text-muted-foreground">{k}:</span>{" "}
                    <span className="line-through opacity-60">[{oldV.join(", ")}]</span>{" "}
                    → <span>[{newV.join(", ")}]</span>
                  </div>
                );
              }
              return (
                <div key={k} className="font-mono">
                  <span className="text-muted-foreground">{k}:</span>{" "}
                  {oldV !== null && oldV !== undefined ? (
                    <span className="line-through opacity-60">{String(oldV)}</span>
                  ) : (
                    <span className="text-muted-foreground italic">(empty)</span>
                  )}
                  {" "}→{" "}
                  {newV !== null && newV !== undefined ? (
                    <span>{String(newV)}</span>
                  ) : (
                    <span className="text-muted-foreground italic">(empty)</span>
                  )}
                </div>
              );
            }
            return <div key={k}>{k}: {String(v)}</div>;
          })}
        </div>
      )}
    </div>
  );
};

export default MenuCsvImport;
